'use client'

import { Component, ReactNode } from 'react'
import { apiFetch } from '@/lib/api-fetch'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error): void {
    // Browser render failures do not appear in server logs by themselves.
    // Send only an allowlisted category and pathname: never the raw message,
    // stack, URL query, customer data, or an API response body.
    const kind = error.message.includes('reconciliation')
      ? 'analytics_cache_schema_mismatch'
      : 'client_render_error'
    void apiFetch('/api/client-errors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ kind, route: window.location.pathname }),
    }).catch(() => undefined)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-full w-full items-center justify-center bg-ivory px-5 py-8">
          <section className="w-full max-w-2xl border border-rule-strong bg-paper p-5 text-left shadow-sm sm:p-7" aria-labelledby="application-error-title">
            <div className="eyebrow !text-ink-muted">Ошибка приложения</div>
            <h1 id="application-error-title" className="mt-2 text-[24px] font-semibold tracking-tight text-ink sm:text-[30px]">Не удалось показать эту страницу</h1>
            <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">Перезагрузите страницу. Если не поможет, обратитесь к администратору.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button onClick={() => window.location.reload()} className="btn btn-sm btn-primary">Перезагрузить</button>
            </div>
          </section>
        </div>
      )
    }
    return this.props.children
  }
}
